/**
 * Stateless shareable-link utilities for TRITAL Care® Capability Statements.
 *
 * Same envelope strategy as /utils/shareLink.js: JSON → base64url → URL fragment,
 * with an FNV-1a integrity checksum and a TTL envelope. Lives on the `/capability-statement`
 * path (configuration) and `/verify` path (the stamped, verifiable snapshot).
 *
 * Why fragments? They never reach the server, so the recipient's browser reads the
 * payload locally — no backend writes, no accidental PII retention, infinite scale.
 */

const SCHEMA_VERSION = 1;
const DEFAULT_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days — capability docs have a longer shelf life than checklists.

function base64urlEncode(str) {
  const bytes = new TextEncoder().encode(str);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64urlDecode(b64url) {
  const pad = "=".repeat((4 - (b64url.length % 4)) % 4);
  const b64 = (b64url + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(36);
}

/**
 * Encode a capability-statement payload into URLs:
 *  - `configUrl` — /capability-statement#p=... (loads the live builder with prefill)
 *  - `verifyUrl` — /verify#p=...             (static verification landing)
 *  - `integrity` — 8-char fnv1a hash for footer line
 */
export function encodeCapability(payload, opts = {}) {
  const now = Date.now();
  const envelope = {
    v: SCHEMA_VERSION,
    iat: now,
    exp: now + (opts.ttlMs || DEFAULT_TTL_MS),
    payload,
  };
  const json = JSON.stringify(envelope);
  const b = base64urlEncode(json);
  const integrity = fnv1a(json);
  const origin = opts.origin || (typeof window !== "undefined" ? window.location.origin : "https://www.tritalcare.com");
  return {
    configUrl: `${origin}/capability-statement#p=${b}&c=${integrity}`,
    verifyUrl: `${origin}/verify#p=${b}&c=${integrity}`,
    integrity,
    issuedAt: new Date(envelope.iat).toISOString(),
    expiresAt: new Date(envelope.exp).toISOString(),
    sizeBytes: b.length,
  };
}

/** Decode a fragment like `p=...&c=...` (with or without leading `#`). */
export function decodeCapability(hash) {
  if (!hash) return { ok: false, reason: "missing" };
  const clean = hash.startsWith("#") ? hash.slice(1) : hash;
  const params = new URLSearchParams(clean);
  const p = params.get("p");
  const c = params.get("c");
  if (!p) return { ok: false, reason: "missing" };
  let json;
  try {
    json = base64urlDecode(p);
  } catch {
    return { ok: false, reason: "malformed" };
  }
  if (c && fnv1a(json) !== c) return { ok: false, reason: "corrupted" };
  let envelope;
  try {
    envelope = JSON.parse(json);
  } catch {
    return { ok: false, reason: "malformed" };
  }
  if (!envelope || envelope.v !== SCHEMA_VERSION) return { ok: false, reason: "version" };
  if (typeof envelope.exp === "number" && Date.now() > envelope.exp) {
    return { ok: false, reason: "expired", meta: { issuedAt: envelope.iat, expiresAt: envelope.exp } };
  }
  return {
    ok: true,
    payload: envelope.payload,
    meta: {
      issuedAt: envelope.iat,
      expiresAt: envelope.exp,
      version: envelope.v,
      integrity: fnv1a(json),
    },
  };
}

/** Default payload — safe starter for first-time visitors. */
export const DEFAULT_CAPABILITY = {
  recipient: { org: "", contact: "", title: "" },
  vehicles: ["direct-prime", "tier1-sub", "msp-vms"],
  naics: ["561320", "561311", "621399", "621610"],
  note: "",
};
